import React, { useState, useEffect } from 'react';
import './App.css';

const API_URL = '';

// Helper function to format 24-hour time to 12-hour format with AM/PM
function formatTime12Hour(time24) {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || 'null'));
  const [currentView, setCurrentView] = useState('calendar');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  }, [token, user]);

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const navigateTo = (view) => {
    setCurrentView(view);
    setMenuOpen(false);
  };

  if (!token) {
    return <Login onLogin={(token, user) => { setToken(token); setUser(user); }} />;
  }

  const getPageTitle = () => {
    switch(currentView) {
      case 'calendar': return 'Calendar';
      case 'journal': return 'Journal';
      case 'recipes': return 'Recipes';
      default: return 'Calendar';
    }
  };

  return (
    <div className="app">
      <header className="header">
        <div className="header-left">
          <button className="hamburger-btn" onClick={() => setMenuOpen(!menuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
          <h1 className="home-link" onClick={() => navigateTo('calendar')}>Family Hub</h1>
        </div>
        <div className="page-title">{getPageTitle()}</div>
        <div className="user-info">
          <span style={{ color: user.color }}>● {user.displayName}</span>
          <button onClick={logout} className="logout-btn">Logout</button>
        </div>
      </header>

      {menuOpen && (
        <div className="menu-overlay" onClick={() => setMenuOpen(false)}>
          <div className="side-menu" onClick={(e) => e.stopPropagation()}>
            <div className="menu-header">
              <h3>Menu</h3>
              <button className="close-menu" onClick={() => setMenuOpen(false)}>×</button>
            </div>
            <nav className="menu-nav">
              <button 
                className={currentView === 'calendar' ? 'active' : ''} 
                onClick={() => navigateTo('calendar')}
              >
                📅 Calendar
              </button>
              <button 
                className={currentView === 'journal' ? 'active' : ''} 
                onClick={() => navigateTo('journal')}
              >
                📝 Journal
              </button>
              <button 
                className={currentView === 'recipes' ? 'active' : ''} 
                onClick={() => navigateTo('recipes')}
              >
                🍽️ Recipes
              </button>
            </nav>
          </div>
        </div>
      )}

      <main className="main-content">
        {currentView === 'calendar' && <Calendar token={token} user={user} selectedDate={selectedDate} setSelectedDate={setSelectedDate} />}
        {currentView === 'journal' && <Journal token={token} user={user} selectedDate={selectedDate} setSelectedDate={setSelectedDate} />}
        {currentView === 'recipes' && <Recipes token={token} user={user} />}
      </main>
    </div>
  );
}

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        throw new Error('Invalid credentials');
      }

      const data = await response.json();
      onLogin(data.token, data.user);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="login-container">
      <form className="login-form" onSubmit={handleSubmit}>
        <h2>Family Hub Login</h2>
        {error && <div className="error">{error}</div>}
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit">Login</button>
      </form>
    </div>
  );
}

function Calendar({ token, user, selectedDate, setSelectedDate }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    loadEvents();
    loadJournalEntries();
  }, [currentMonth]);

  const loadEvents = async () => {
    const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).toISOString().split('T')[0];
    const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).toISOString().split('T')[0];

    const response = await fetch(`${API_URL}/api/events?start=${start}&end=${end}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setEvents(data);
  };

  const loadJournalEntries = async () => {
    const response = await fetch(`${API_URL}/api/journal`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setJournalEntries(data);
  };

  const openModal = (date) => {
    setSelectedDate(date);
    setShowModal(true);
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => e.date === dateStr);
  };

  const getJournalForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return journalEntries.filter(e => e.date === dateStr);
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const changeMonthYear = (month, year) => {
    setCurrentMonth(new Date(year, month, 1));
    setShowDatePicker(false);
  };

  return (
    <div className="calendar-view">
      <div className="calendar-header">
        <button onClick={prevMonth}>←</button>
        <h2>
          <span className="date-text" onClick={() => setShowDatePicker(!showDatePicker)}>
            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} ▾
          </span>
        </h2>
        <button onClick={nextMonth}>→</button>
      </div>

      {showDatePicker && (
        <DatePicker 
          currentMonth={currentMonth} 
          onSelect={changeMonthYear}
          onClose={() => setShowDatePicker(false)}
        />
      )}

      <div className="calendar-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="calendar-day-header">{day}</div>
        ))}
        {getDaysInMonth().map((date, idx) => {
          const dayEvents = getEventsForDate(date);
          const dayJournal = getJournalForDate(date);
          const isToday = date && date.toDateString() === new Date().toDateString();

          return (
            <div
              key={idx}
              className={`calendar-day ${!date ? 'empty' : ''} ${isToday ? 'today' : ''}`}
              onClick={() => date && openModal(date)}
            >
              {date && (
                <>
                  <div className="day-number">{date.getDate()}</div>
                  {dayEvents.map(event => (
                    <div key={event.id} className="event-indicator" style={{ backgroundColor: event.color }}>
                      {event.title} {event.recurring === 1 && '🔁'}
                    </div>
                  ))}
                  {dayJournal.length > 0 && (
                    <div className="journal-indicator">📝 {dayJournal.length}</div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {showModal && (
        <DayModal
          date={selectedDate}
          events={getEventsForDate(selectedDate)}
          journalEntries={getJournalForDate(selectedDate)}
          onClose={() => setShowModal(false)}
          onRefresh={() => { loadEvents(); loadJournalEntries(); }}
          token={token}
          user={user}
        />
      )}
    </div>
  );
}

function DatePicker({ currentMonth, onSelect, onClose }) {
  const [selectedMonth, setSelectedMonth] = useState(currentMonth.getMonth());
  const [selectedYear, setSelectedYear] = useState(currentMonth.getFullYear());

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = [];
  for (let y = selectedYear - 50; y <= selectedYear + 50; y++) {
    years.push(y);
  }

  return (
    <div className="date-picker-overlay" onClick={onClose}>
      <div className="date-picker" onClick={(e) => e.stopPropagation()}>
        <h3>Select Month & Year</h3>
        <div className="date-picker-selects">
          <select 
            value={selectedMonth} 
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          >
            {months.map((month, idx) => (
              <option key={idx} value={idx}>{month}</option>
            ))}
          </select>
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <div className="date-picker-buttons">
          <button onClick={onClose} className="cancel-btn">Cancel</button>
          <button onClick={() => onSelect(selectedMonth, selectedYear)} className="confirm-btn">
            Go to Date
          </button>
        </div>
      </div>
    </div>
  );
}

function DayModal({ date, events, journalEntries, onClose, onRefresh, token, user }) {
  const [activeTab, setActiveTab] = useState('view');
  const [eventForm, setEventForm] = useState({ title: '', time: '', description: '', recurring: false });
  const [journalForm, setJournalForm] = useState({ title: '', content: '', isPrivate: false, tags: '', image: null });
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingJournal, setEditingJournal] = useState(null);

  const handleAddEvent = async (e) => {
    e.preventDefault();
    const response = await fetch(`${API_URL}/api/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        ...eventForm,
        date: date.toISOString().split('T')[0]
      })
    });

    if (response.ok) {
      setEventForm({ title: '', time: '', description: '', recurring: false });
      setActiveTab('view');
      onRefresh();
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    const response = await fetch(`${API_URL}/api/events/${editingEvent.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        ...eventForm,
        date: date.toISOString().split('T')[0]
      })
    });

    if (response.ok) {
      setEventForm({ title: '', time: '', description: '', recurring: false });
      setEditingEvent(null);
      setActiveTab('view');
      onRefresh();
    }
  };

  const startEditingEvent = (event) => {
    setEventForm({
      title: event.title,
      time: event.time || '',
      description: event.description || '',
      recurring: event.recurring === 1
    });
    setEditingEvent(event);
    setActiveTab('event');
  };

  const cancelEditingEvent = () => {
    setEventForm({ title: '', time: '', description: '', recurring: false });
    setEditingEvent(null);
  };

  const handleAddJournal = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', journalForm.title);
    formData.append('content', journalForm.content);
    formData.append('date', date.toISOString().split('T')[0]);
    formData.append('isPrivate', journalForm.isPrivate);
    formData.append('tags', JSON.stringify(journalForm.tags.split(',').map(t => t.trim()).filter(t => t)));
    if (journalForm.image) {
      formData.append('image', journalForm.image);
    }

    const response = await fetch(`${API_URL}/api/journal`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (response.ok) {
      setJournalForm({ title: '', content: '', isPrivate: false, tags: '', image: null });
      setActiveTab('view');
      onRefresh();
    }
  };

  const handleUpdateJournal = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', journalForm.title);
    formData.append('content', journalForm.content);
    formData.append('date', date.toISOString().split('T')[0]);
    formData.append('isPrivate', journalForm.isPrivate);
    formData.append('tags', JSON.stringify(journalForm.tags.split(',').map(t => t.trim()).filter(t => t)));
    if (journalForm.image) {
      formData.append('image', journalForm.image);
    }
    if (journalForm.removeImage) {
      formData.append('removeImage', 'true');
    }

    const response = await fetch(`${API_URL}/api/journal/${editingJournal.id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (response.ok) {
      setJournalForm({ title: '', content: '', isPrivate: false, tags: '', image: null });
      setEditingJournal(null);
      setActiveTab('view');
      onRefresh();
    }
  };

  const startEditingJournal = (entry) => {
    setJournalForm({
      title: entry.title,
      content: entry.content,
      isPrivate: entry.is_private === 1,
      tags: entry.tags ? entry.tags.join(', ') : '',
      image: null,
      existingImage: entry.image_url,
      removeImage: false
    });
    setEditingJournal(entry);
    setActiveTab('journal');
  };

  const cancelEditingJournal = () => {
    setJournalForm({ title: '', content: '', isPrivate: false, tags: '', image: null });
    setEditingJournal(null);
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    await fetch(`${API_URL}/api/events/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    onRefresh();
  };

  const deleteJournal = async (id) => {
    if (!window.confirm('Delete this journal entry?')) return;
    await fetch(`${API_URL}/api/journal/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    onRefresh();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <div className="modal-tabs">
          <button className={activeTab === 'view' ? 'active' : ''} onClick={() => setActiveTab('view')}>View</button>
          <button className={activeTab === 'event' ? 'active' : ''} onClick={() => setActiveTab('event')}>
            {editingEvent ? 'Edit Event' : 'Add Event'}
          </button>
          <button className={activeTab === 'journal' ? 'active' : ''} onClick={() => setActiveTab('journal')}>
            {editingJournal ? 'Edit Journal' : 'Add Journal'}
          </button>
        </div>

        <div className="modal-content">
          {activeTab === 'view' && (
            <div>
              <h4>Events</h4>
              {events.length === 0 ? (
                <p className="empty">No events for this day</p>
              ) : (
                events.map(event => (
                  <div key={event.id} className="item-card">
                    <div className="item-header">
                      <strong style={{ color: event.color }}>
                        {event.title}
                        {event.recurring === 1 && ' 🔁'}
                      </strong>
                      {event.user_id === user.id && (
                        <div className="item-actions">
                          <button onClick={() => startEditingEvent(event)} className="edit-btn">Edit</button>
                          <button onClick={() => deleteEvent(event.id)} className="delete-btn">Delete</button>
                        </div>
                      )}
                    </div>
                    {event.time && <div className="item-time">🕐 {formatTime12Hour(event.time)}</div>}
                    {event.description && <p>{event.description}</p>}
                    {event.recurring === 1 && <div className="item-time">🔁 Repeats annually</div>}
                    <div className="item-author">by {event.display_name}</div>
                  </div>
                ))
              )}

              <h4>Journal Entries</h4>
              {journalEntries.length === 0 ? (
                <p className="empty">No journal entries for this day</p>
              ) : (
                journalEntries.map(entry => (
                  <div key={entry.id} className="item-card">
                    <div className="item-header">
                      <strong>{entry.title}</strong>
                      {entry.user_id === user.id && (
                        <div className="item-actions">
                          <button onClick={() => startEditingJournal(entry)} className="edit-btn">Edit</button>
                          <button onClick={() => deleteJournal(entry.id)} className="delete-btn">Delete</button>
                        </div>
                      )}
                    </div>
                    {entry.is_private === 1 && <span className="private-badge">🔒 Private</span>}
                    {entry.image_url && <img src={`${API_URL}${entry.image_url}`} alt={entry.title} className="entry-image" />}
                    <p>{entry.content}</p>
                    {entry.tags && entry.tags.length > 0 && (
                      <div className="tags">
                        {entry.tags.map((tag, i) => <span key={i} className="tag">{tag}</span>)}
                      </div>
                    )}
                    <div className="item-author">by {entry.display_name}</div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'event' && (
            <form onSubmit={editingEvent ? handleUpdateEvent : handleAddEvent} className="form">
              {editingEvent && (
                <div className="edit-notice">Editing event: {editingEvent.title}</div>
              )}
              <input
                type="text"
                placeholder="Event title"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                required
              />
              <input
                type="time"
                value={eventForm.time}
                onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
              />
              <textarea
                placeholder="Description (optional)"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                rows={3}
              />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={eventForm.recurring}
                  onChange={(e) => setEventForm({ ...eventForm, recurring: e.target.checked })}
                />
                Recurring annually (birthdays, anniversaries, holidays)
              </label>
              <div className="form-buttons">
                <button type="submit">{editingEvent ? 'Update Event' : 'Add Event'}</button>
                {editingEvent && (
                  <button type="button" onClick={cancelEditingEvent} className="cancel-btn">Cancel</button>
                )}
              </div>
            </form>
          )}

          {activeTab === 'journal' && (
            <form onSubmit={editingJournal ? handleUpdateJournal : handleAddJournal} className="form">
              {editingJournal && (
                <div className="edit-notice">Editing journal entry: {editingJournal.title}</div>
              )}
              <input
                type="text"
                placeholder="Entry title"
                value={journalForm.title}
                onChange={(e) => setJournalForm({ ...journalForm, title: e.target.value })}
                required
              />
              <textarea
                placeholder="Write your journal entry..."
                value={journalForm.content}
                onChange={(e) => setJournalForm({ ...journalForm, content: e.target.value })}
                rows={6}
                required
              />
              <input
                type="text"
                placeholder="Tags (comma-separated)"
                value={journalForm.tags}
                onChange={(e) => setJournalForm({ ...journalForm, tags: e.target.value })}
              />
              {editingJournal && journalForm.existingImage && !journalForm.removeImage && (
                <div className="existing-image-preview">
                  <img src={`${API_URL}${journalForm.existingImage}`} alt="Current" />
                  <button 
                    type="button" 
                    onClick={() => setJournalForm({ ...journalForm, removeImage: true })}
                    className="remove-image-btn"
                  >
                    Remove Image
                  </button>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setJournalForm({ ...journalForm, image: e.target.files[0], removeImage: false })}
              />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={journalForm.isPrivate}
                  onChange={(e) => setJournalForm({ ...journalForm, isPrivate: e.target.checked })}
                />
                Mark as private (only you can see this)
              </label>
              <div className="form-buttons">
                <button type="submit">{editingJournal ? 'Update Entry' : 'Add Journal Entry'}</button>
                {editingJournal && (
                  <button type="button" onClick={cancelEditingJournal} className="cancel-btn">Cancel</button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Journal({ token, user, selectedDate, setSelectedDate }) {
  const [entries, setEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [journalForm, setJournalForm] = useState({ 
    title: '', 
    content: '', 
    date: new Date().toISOString().split('T')[0],
    isPrivate: false, 
    tags: '', 
    image: null 
  });

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    const response = await fetch(`${API_URL}/api/journal`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setEntries(data);
  };

  const filteredEntries = entries.filter(entry =>
    entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (entry.tags && entry.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  const openEntry = (entry) => {
    setSelectedEntry(entry);
    setShowViewModal(true);
  };

  const startEditingEntry = (entry) => {
    setJournalForm({
      title: entry.title,
      content: entry.content,
      date: entry.date,
      isPrivate: entry.is_private === 1,
      tags: entry.tags ? entry.tags.join(', ') : '',
      image: null,
      existingImage: entry.image_url,
      removeImage: false
    });
    setEditingEntry(entry);
    setShowAddModal(true);
    setShowViewModal(false);
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    const response = await fetch(`${API_URL}/api/journal/${entryId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      loadEntries();
      setShowViewModal(false);
    }
  };

  const handleAddJournal = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', journalForm.title);
    formData.append('content', journalForm.content);
    formData.append('date', journalForm.date);
    formData.append('isPrivate', journalForm.isPrivate);
    formData.append('tags', JSON.stringify(journalForm.tags.split(',').map(t => t.trim()).filter(t => t)));
    if (journalForm.image) {
      formData.append('image', journalForm.image);
    }

    const url = editingEntry 
      ? `${API_URL}/api/journal/${editingEntry.id}`
      : `${API_URL}/api/journal`;
    
    const method = editingEntry ? 'PUT' : 'POST';

    if (editingEntry && journalForm.removeImage) {
      formData.append('removeImage', 'true');
    }

    const response = await fetch(url, {
      method,
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (response.ok) {
      setJournalForm({ 
        title: '', 
        content: '', 
        date: new Date().toISOString().split('T')[0],
        isPrivate: false, 
        tags: '', 
        image: null 
      });
      setShowAddModal(false);
      setEditingEntry(null);
      loadEntries();
    }
  };

  const resetForm = () => {
    setJournalForm({ 
      title: '', 
      content: '', 
      date: new Date().toISOString().split('T')[0],
      isPrivate: false, 
      tags: '', 
      image: null 
    });
    setEditingEntry(null);
  };

  return (
    <div className="content-view">
      <div className="view-header">
        <h2>Journal Entries</h2>
        <div className="header-actions">
          <input
            type="text"
            placeholder="Search entries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button onClick={() => setShowAddModal(true)} className="add-button">
            + Add Entry
          </button>
        </div>
      </div>

      <div className="items-grid">
        {filteredEntries.map(entry => (
          <div key={entry.id} className="item-card clickable" onClick={() => openEntry(entry)}>
            <div className="item-header">
              <strong>{entry.title}</strong>
              {entry.is_private === 1 && <span className="private-badge">🔒</span>}
            </div>
            <div className="item-date">{new Date(entry.date).toLocaleDateString()}</div>
            {entry.image_url && <img src={`${API_URL}${entry.image_url}`} alt={entry.title} className="entry-image" />}
            <p>{entry.content.substring(0, 200)}{entry.content.length > 200 ? '...' : ''}</p>
            {entry.tags && entry.tags.length > 0 && (
              <div className="tags">
                {entry.tags.map((tag, i) => <span key={i} className="tag">{tag}</span>)}
              </div>
            )}
            <div className="item-author" style={{ color: entry.color }}>by {entry.display_name}</div>
          </div>
        ))}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); resetForm(); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingEntry ? 'Edit Journal Entry' : 'Add Journal Entry'}</h3>
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="close-btn">×</button>
            </div>
            <div className="modal-content">
              <form onSubmit={handleAddJournal} className="form">
                <input
                  type="date"
                  value={journalForm.date}
                  onChange={(e) => setJournalForm({...journalForm, date: e.target.value})}
                  required
                />
                <input
                  type="text"
                  placeholder="Title"
                  value={journalForm.title}
                  onChange={(e) => setJournalForm({...journalForm, title: e.target.value})}
                  required
                />
                <textarea
                  placeholder="Write your journal entry..."
                  value={journalForm.content}
                  onChange={(e) => setJournalForm({...journalForm, content: e.target.value})}
                  rows={6}
                  required
                />
                <input
                  type="text"
                  placeholder="Tags (comma-separated)"
                  value={journalForm.tags}
                  onChange={(e) => setJournalForm({...journalForm, tags: e.target.value})}
                />
                {editingEntry && journalForm.existingImage && !journalForm.removeImage && (
                  <div className="existing-image-preview">
                    <img src={`${API_URL}${journalForm.existingImage}`} alt="Current" />
                    <button 
                      type="button" 
                      onClick={() => setJournalForm({...journalForm, removeImage: true})}
                      className="remove-image-btn"
                    >
                      Remove Image
                    </button>
                  </div>
                )}
                <label htmlFor="journal-file-input" className="file-input-wrapper">
                  <span className="file-label">Choose File</span>
                  <span className="file-name">
                    {journalForm.image ? journalForm.image.name : 'No file chosen'}
                  </span>
                  <input
                    id="journal-file-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setJournalForm({...journalForm, image: e.target.files[0], removeImage: false})}
                    className="hidden-file-input"
                  />
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={journalForm.isPrivate}
                    onChange={(e) => setJournalForm({...journalForm, isPrivate: e.target.checked})}
                  />
                  Mark as private (only you can see this)
                </label>
                <button type="submit" className="submit-btn">
                  {editingEntry ? 'Update Entry' : 'Add Journal Entry'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {showViewModal && selectedEntry && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedEntry.title}</h3>
              <button onClick={() => setShowViewModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-content">
              <div className="item-date">{new Date(selectedEntry.date).toLocaleDateString()}</div>
              {selectedEntry.is_private === 1 && <span className="private-badge">🔒 Private</span>}
              {selectedEntry.image_url && (
                <img src={`${API_URL}${selectedEntry.image_url}`} alt={selectedEntry.title} className="recipe-image-large" />
              )}
              <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', marginTop: '16px' }}>{selectedEntry.content}</p>
              {selectedEntry.tags && selectedEntry.tags.length > 0 && (
                <div className="tags" style={{ marginTop: '16px' }}>
                  {selectedEntry.tags.map((tag, i) => <span key={i} className="tag">{tag}</span>)}
                </div>
              )}
              <div className="item-author" style={{ color: selectedEntry.color, marginTop: '16px' }}>
                by {selectedEntry.display_name}
              </div>
              {selectedEntry.user_id === user.id && (
                <div className="modal-actions" style={{ marginTop: '24px' }}>
                  <button onClick={() => startEditingEntry(selectedEntry)} className="edit-btn">
                    Edit Entry
                  </button>
                  <button onClick={() => handleDeleteEntry(selectedEntry.id)} className="delete-btn">
                    Delete Entry
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Recipes({ token, user }) {
  const [recipes, setRecipes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeForm, setRecipeForm] = useState({ title: '', ingredients: '', instructions: '', tags: '', image: null });
  const [editingRecipe, setEditingRecipe] = useState(null);

  useEffect(() => {
    loadRecipes();
  }, []);

  const loadRecipes = async () => {
    const response = await fetch(`${API_URL}/api/recipes`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setRecipes(data);
  };

  const handleAddRecipe = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', recipeForm.title);
    formData.append('ingredients', recipeForm.ingredients);
    formData.append('instructions', recipeForm.instructions);
    formData.append('tags', JSON.stringify(recipeForm.tags.split(',').map(t => t.trim()).filter(t => t)));
    if (recipeForm.image) {
      formData.append('image', recipeForm.image);
    }

    const response = await fetch(`${API_URL}/api/recipes`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (response.ok) {
      setRecipeForm({ title: '', ingredients: '', instructions: '', tags: '', image: null });
      setShowModal(false);
      loadRecipes();
    }
  };

  const handleUpdateRecipe = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', recipeForm.title);
    formData.append('ingredients', recipeForm.ingredients);
    formData.append('instructions', recipeForm.instructions);
    formData.append('tags', JSON.stringify(recipeForm.tags.split(',').map(t => t.trim()).filter(t => t)));
    if (recipeForm.image) {
      formData.append('image', recipeForm.image);
    }
    if (recipeForm.removeImage) {
      formData.append('removeImage', 'true');
    }

    const response = await fetch(`${API_URL}/api/recipes/${editingRecipe.id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (response.ok) {
      setRecipeForm({ title: '', ingredients: '', instructions: '', tags: '', image: null });
      setEditingRecipe(null);
      setShowModal(false);
      setSelectedRecipe(null);
      loadRecipes();
    }
  };

  const startEditingRecipe = (recipe) => {
    setRecipeForm({
      title: recipe.title,
      ingredients: recipe.ingredients,
      instructions: recipe.instructions,
      tags: recipe.tags ? recipe.tags.join(', ') : '',
      image: null,
      existingImage: recipe.image_url,
      removeImage: false
    });
    setEditingRecipe(recipe);
    setSelectedRecipe(null);
    setShowModal(true);
  };

const cancelEditingRecipe = () => {
    setRecipeForm({ title: '', ingredients: '', instructions: '', tags: '', image: null });
    setEditingRecipe(null);
    setShowModal(false);
  };

  const deleteRecipe = async (id) => {
    if (!window.confirm('Delete this recipe?')) return;
    await fetch(`${API_URL}/api/recipes/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    setSelectedRecipe(null);
    loadRecipes();
  };

  const filteredRecipes = recipes.filter(recipe =>
    recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    recipe.ingredients.toLowerCase().includes(searchTerm.toLowerCase()) ||
    recipe.instructions.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (recipe.tags && recipe.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  return (
    <div className="content-view">
      <div className="view-header">
        <h2>Recipes</h2>
        <div className="header-actions">
          <input
            type="text"
            placeholder="Search recipes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        <button onClick={() => { setShowModal(true); setRecipeForm({ title: '', ingredients: '', instructions: '', tags: '', image: null }); setEditingRecipe(null); }} className="add-btn">+ Add Recipe</button>
        </div>
      </div>

      <div className="items-grid">
        {filteredRecipes.map(recipe => (
          <div key={recipe.id} className="item-card" onClick={() => setSelectedRecipe(recipe)}>
            {recipe.image_url && <img src={`${API_URL}${recipe.image_url}`} alt={recipe.title} className="recipe-image" />}
            <strong>{recipe.title}</strong>
            <p>{recipe.ingredients.substring(0, 100)}...</p>
            {recipe.tags && recipe.tags.length > 0 && (
              <div className="tags">
                {recipe.tags.map((tag, i) => <span key={i} className="tag">{tag}</span>)}
              </div>
            )}
            <div className="item-author" style={{ color: recipe.color }}>by {recipe.display_name}</div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingRecipe(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRecipe ? 'Edit Recipe' : 'Add New Recipe'}</h3>
              <button onClick={() => { setShowModal(false); setEditingRecipe(null); }} className="close-btn">×</button>
            </div>
            <div className="modal-content">
              <form onSubmit={editingRecipe ? handleUpdateRecipe : handleAddRecipe} className="form">
                {editingRecipe && (
                  <div className="edit-notice">Editing recipe: {editingRecipe.title}</div>
                )}
                <input
                  type="text"
                  placeholder="Recipe title"
                  value={recipeForm.title}
                  onChange={(e) => setRecipeForm({ ...recipeForm, title: e.target.value })}
                  required
                />
                <textarea
                  placeholder="Ingredients (one per line)"
                  value={recipeForm.ingredients}
                  onChange={(e) => setRecipeForm({ ...recipeForm, ingredients: e.target.value })}
                  rows={6}
                  required
                />
                <textarea
                  placeholder="Instructions"
                  value={recipeForm.instructions}
                  onChange={(e) => setRecipeForm({ ...recipeForm, instructions: e.target.value })}
                  rows={8}
                  required
                />
                <input
                  type="text"
                  placeholder="Tags (comma-separated, e.g., italian, pasta, quick)"
                  value={recipeForm.tags}
                  onChange={(e) => setRecipeForm({ ...recipeForm, tags: e.target.value })}
                />
                {editingRecipe && recipeForm.existingImage && !recipeForm.removeImage && (
                  <div className="existing-image-preview">
                    <img src={`${API_URL}${recipeForm.existingImage}`} alt="Current" />
                    <button 
                      type="button" 
                      onClick={() => setRecipeForm({ ...recipeForm, removeImage: true })}
                      className="remove-image-btn"
                    >
                      Remove Image
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setRecipeForm({ ...recipeForm, image: e.target.files[0], removeImage: false })}
                />
                <div className="form-buttons">
                  <button type="submit">{editingRecipe ? 'Update Recipe' : 'Add Recipe'}</button>
                  {editingRecipe && (
                    <button type="button" onClick={cancelEditingRecipe} className="cancel-btn">Cancel</button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {selectedRecipe && (
        <div className="modal-overlay" onClick={() => setSelectedRecipe(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedRecipe.title}</h3>
              <button onClick={() => setSelectedRecipe(null)} className="close-btn">×</button>
            </div>
            <div className="modal-content recipe-detail">
              {selectedRecipe.image_url && <img src={`${API_URL}${selectedRecipe.image_url}`} alt={selectedRecipe.title} className="recipe-image-large" />}
              
              <h4>Ingredients</h4>
              <pre className="recipe-section">{selectedRecipe.ingredients}</pre>
              
              <h4>Instructions</h4>
              <pre className="recipe-section">{selectedRecipe.instructions}</pre>
              
              {selectedRecipe.tags && selectedRecipe.tags.length > 0 && (
                <div className="tags">
                  {selectedRecipe.tags.map((tag, i) => <span key={i} className="tag">{tag}</span>)}
                </div>
              )}
              
              <div className="item-author" style={{ color: selectedRecipe.color }}>Added by {selectedRecipe.display_name}</div>
              
              {selectedRecipe.user_id === user.id && (
                <div className="recipe-actions">
                  <button onClick={() => startEditingRecipe(selectedRecipe)} className="edit-btn">Edit Recipe</button>
                  <button onClick={() => deleteRecipe(selectedRecipe.id)} className="delete-btn">Delete Recipe</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
